/**
 * Proof-of-login predicate (CRE-783).
 *
 * The single safety-critical decision behind the auth-rescue gate: may a
 * session that already exists be treated as the result of THIS sign-in
 * attempt? It may ONLY when the session carries the exact `auth_attempt_id`
 * (ULID) that this attempt minted and stamped into the iron-session.
 *
 * Returns `true` (adopt/relay) only when BOTH a non-empty attempt id and a
 * non-empty session stamp are present AND they are strictly equal. Every
 * other case — absent attempt, absent/empty stamp, mismatch — returns
 * `false` (suppress). This is the fail-closed default: an unproven session is
 * never adopted, so a canceled popup or a pre-existing cookie can never be
 * pushed into the browser extension.
 *
 * Kept as a tiny pure function so the invariant is unit-testable (the vitest
 * matrix at `tests/unit/lib/auth-attempt-match.test.ts` locks this contract)
 * and so the same decision is expressed identically across every gate site.
 * The landing iframe rescue (`use-auth.ts`) uses THIS copy; the four webapp
 * gate sites (`use-sign-in-flow.ts`, `verify-page.tsx`,
 * `magic-link-verify-page.tsx`, `callback-page.tsx`) use a byte-for-byte
 * duplicate at `apps/extension-webapp/src/lib/auth-attempt-match.ts` (the apps
 * don't share a runtime package; @repo/types is type-only and bundled by
 * mobile's Metro build, so the predicate is duplicated rather than
 * centralized). If you change this, change the webapp copy too.
 */
export const isSessionProvenByAttempt = (
  sessionStamp: string | null | undefined,
  attemptId: string | null | undefined,
): boolean => !!attemptId && !!sessionStamp && sessionStamp === attemptId;
